import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorPage } from './error-page';

/**
 * Default fallback rendered when no custom `fallback` prop is given. A functional
 * component so it can use `useTranslation` (the class ErrorBoundary cannot) — this keeps
 * the fallback copy reactive to language switches.
 */
function DefaultErrorFallback({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('common');
  return (
    <ErrorPage
      title={t('errorBoundary.title')}
      message={t('errorBoundary.message')}
      onRetry={onRetry}
      showHomeLink
    />
  );
}

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Custom fallback UI to show on error */
  fallback?: ReactNode;
  /** Callback when error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * React Error Boundary to prevent white screen crashes.
 *
 * Features:
 * - Catches JavaScript errors in child component tree
 * - Displays user-friendly error page
 * - Logs errors to monitoring service
 * - Provides "Try Again" action
 *
 * @see Spec 0012 Section 7.4 - Error Boundary (OPS-01 - CRITICAL)
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (import.meta.env.DEV) console.error('ErrorBoundary caught:', error, errorInfo);

    // Delegate to monitoring callback if provided
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return this.props.fallback ?? <DefaultErrorFallback onRetry={this.handleRetry} />;
    }
    return this.props.children;
  }
}
